import { useState } from "react";
import toast from "react-hot-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useScheduleIssues } from "../api/use-issues";
import { useRuns } from "../api/use-runs";
import { useRevalidateSchedule } from "../api/use-schedules";
import { groupIssuesByCodeSeverity, sumIssueUnits } from "../lib/group-issues";
import {
  MAX_OVERLAP_TOLERANCE,
  useOverlapTolerance,
} from "../lib/use-overlap-tolerance";
import type { IssueStage } from "../types";
import type { CourseFocusHandler } from "./course-focus-handler";
import { ForcePlaceModal } from "./force-place-modal";
import { ValidationIssuesCard } from "./validation-issues-card";

/**
 * The three buckets the issue list is split into, in display order. Each maps
 * a backend {@link IssueStage} to a human-readable section heading:
 * schedule (calendar import) → course requests → run (post-run scheduler).
 */
const ISSUE_SECTIONS: ReadonlyArray<{ stage: IssueStage; label: string }> = [
  { stage: "CalendarImport", label: "Schedule issues" },
  { stage: "CourseRequestsImport", label: "Course request issues" },
  { stage: "PostRun", label: "Run issues" },
];

interface ValidationIssuesTabProps {
  scheduleId: string;
  /**
   * Whether the current user may re-run validations (mirrors the backend
   * `OrgAdminOrAbove` policy on the revalidate endpoint). When true, a
   * "Re-run validations" button is shown in the banner.
   */
  canRevalidate?: boolean;
  /**
   * Triggered when a user clicks "Reassign" on an issue row that carries
   * both `studentIdentifier` and `courseId`. Hidden for Viewer roles by the
   * downstream card; this callback is also a no-op for issues without the
   * required context fields. Slice 4 wires this to the page-level Reassign
   * modal.
   */
  onReassign?: (
    studentIdentifier: string,
    courseId: string,
    sectionLabel: string | null,
  ) => void;
  /**
   * Triggered when a user clicks a course reference in an issue's context line
   * or detail list. Slice 5 wires this to the page-level calendar focus so the
   * calendar tab opens and highlights the course.
   */
  onCourseFocus?: CourseFocusHandler;
}

/**
 * Schedule Home → Validation Issues tab. Combines the issues list (which
 * already merges Stage 1/2/3 by virtue of the backend's stage-agnostic GET)
 * with the runs list, which is used purely to drive the "Run #N completed"
 * banner copy.
 *
 * Phase 4 banner format:
 *   "Run #N completed · X issues across Y categories"
 * where:
 *   N = total number of recorded runs (succeeded or otherwise). A truly
 *       stable run-number is deferred to Phase 5 (the planned `RunSummary`
 *       gains a `runNumber` field then); using `runs.length` here is fine
 *       because the list is sorted DESC and we want the most recent.
 *   X = total issue count across all stages.
 *   Y = distinct (code, severity) pairs — what {@link groupIssuesByCodeSeverity}
 *       returns.
 */
export function ValidationIssuesTab({
  scheduleId,
  canRevalidate = false,
  onReassign,
  onCourseFocus,
}: ValidationIssuesTabProps) {
  const { tolerance, setTolerance } = useOverlapTolerance();
  const issuesQuery = useScheduleIssues(scheduleId, {
    toleranceMinutes: tolerance,
  });
  const runsQuery = useRuns(scheduleId);
  const revalidate = useRevalidateSchedule();
  const [forcePlaceOpen, setForcePlaceOpen] = useState(false);

  const handleRevalidate = () => {
    if (revalidate.isPending) return;
    revalidate.mutate(scheduleId, {
      onSuccess: () => toast.success("Validations re-run."),
      onError: () => toast.error("Failed to re-run validations."),
    });
  };

  if (issuesQuery.isLoading || runsQuery.isLoading) {
    return (
      <div
        data-testid="validation-issues-tab-loading"
        className="py-16 flex justify-center"
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (issuesQuery.isError) {
    return (
      <div
        data-testid="validation-issues-tab-error"
        className="bg-surface border border-red-500/30 rounded-lg p-6 flex items-center justify-between gap-4"
      >
        <p className="text-sm text-red-400">
          {issuesQuery.error instanceof Error
            ? issuesQuery.error.message
            : "Failed to load validation issues."}
        </p>
        <button
          type="button"
          onClick={() => issuesQuery.refetch()}
          data-testid="validation-issues-tab-retry"
          className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-3 py-1.5 text-[13px] font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  const issues = issuesQuery.data ?? [];
  const runs = runsQuery.data ?? [];
  const succeededRuns = runs.filter((r) => r.status === "Succeeded");
  const hasSucceededRun = succeededRuns.length > 0;

  // Force-place needs OrgAdmin+ (same policy as revalidate) AND the LATEST run
  // to be Succeeded (the backend gate rejects otherwise). Runs are DESC-sorted.
  const latestRun = runs[0];
  const canForcePlace = canRevalidate && latestRun?.status === "Succeeded";

  const runNumber = runs.length;
  const groups = groupIssuesByCodeSeverity(issues);
  // Student-weighted so this total reconciles with the group badges and the
  // Run button chips: a "could not place" course counts each affected student,
  // every other issue counts once. (Not issues.length — see sumIssueUnits.)
  const issueCount = sumIssueUnits(issues);
  const groupCount = groups.length;

  // Pre-run, the issue list still surfaces Stage 1 (CalendarImport) and
  // Stage 2 (CourseRequestsImport) findings — those are produced on import
  // and don't require a run. The banner copy just shifts to "pre-run" so
  // users know they're not looking at scheduler output.
  const bannerLabel = hasSucceededRun
    ? `Run #${runNumber} completed`
    : "Pre-run validation";

  return (
    <div
      data-testid="schedule-tab-issues-content"
      className="flex flex-col gap-4"
    >
      <div
        data-testid="validation-issues-banner"
        className="bg-blue-900/40 border border-blue-700/60 rounded-lg px-4 py-3 flex items-center gap-3 text-[13px] text-blue-100"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className="text-blue-200 shrink-0"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <span>
          {bannerLabel} ·{" "}
          <strong>
            {issueCount} {issueCount === 1 ? "issue" : "issues"}
          </strong>{" "}
          across {groupCount} {groupCount === 1 ? "category" : "categories"}
        </span>

        {canRevalidate && (
          <button
            type="button"
            onClick={handleRevalidate}
            disabled={revalidate.isPending}
            data-testid="validation-issues-revalidate"
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 bg-surface-2 hover:bg-surface-2/80 disabled:opacity-60 text-white rounded-md px-3 py-1.5 text-[13px] font-medium"
          >
            {revalidate.isPending && <LoadingSpinner size="sm" />}
            {revalidate.isPending ? "Re-running…" : "Re-run validations"}
          </button>
        )}
      </div>

      {/* "Accept small overlaps" toolbar. Adjust the tolerance (persisted
          client-side) to hide within-tolerance conflicts live, then bulk-place
          the students they were blocking. OrgAdmin+ and a Succeeded latest run
          only. */}
      {canForcePlace && (
        <div
          data-testid="force-place-toolbar"
          className="flex flex-wrap items-center gap-3 bg-surface border border-border-subtle rounded-lg px-4 py-2.5 text-[13px] text-text-soft"
        >
          <label
            htmlFor="overlap-tolerance-input"
            className="flex items-center gap-2"
          >
            <span className="text-text-muted">Overlap tolerance</span>
            <input
              id="overlap-tolerance-input"
              data-testid="overlap-tolerance-input"
              type="number"
              min={0}
              max={MAX_OVERLAP_TOLERANCE}
              value={tolerance}
              onChange={(e) => setTolerance(Number.parseInt(e.target.value, 10))}
              className="w-16 bg-page border border-border-subtle rounded-md px-2 py-1 text-white text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            />
            <span className="text-text-muted">min</span>
          </label>
          <button
            type="button"
            onClick={() => setForcePlaceOpen(true)}
            disabled={tolerance <= 0}
            data-testid="force-place-open"
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 bg-surface-2 hover:bg-surface-2/80 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-md px-3 py-1.5 font-medium"
          >
            Accept small overlaps
          </button>
        </div>
      )}

      {/* Post-run (scheduler) issues are tied to the latest run and are cleared
          when the calendar is refreshed. Users were confused when errors
          "disappeared" after a refresh — this note explains they come back on
          the next run. Only relevant once a run exists. */}
      {hasSucceededRun && (
        <p
          data-testid="validation-issues-rerun-note"
          className="-mt-2 px-1 text-[12px] text-text-muted"
        >
          Run (scheduler) issues reflect the latest run and are cleared when you
          refresh the calendar. Re-run the scheduler to regenerate them.
        </p>
      )}

      {issueCount === 0 ? (
        <div
          data-testid="validation-issues-clean"
          className="bg-surface border border-border-subtle rounded-lg p-6 text-sm text-text-muted text-center"
        >
          {hasSucceededRun
            ? "No issues found — the run completed cleanly."
            : "No issues found on import. Run the scheduler to surface assignment-time issues."}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {ISSUE_SECTIONS.map(({ stage, label }) => {
            const sectionIssues = issues.filter((i) => i.stage === stage);
            if (sectionIssues.length === 0) return null;
            return (
              <section
                key={stage}
                data-testid={`validation-section-${stage}`}
                className="flex flex-col gap-3"
              >
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {label}
                  <span
                    data-testid={`validation-section-count-${stage}`}
                    className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-surface-2 text-text-soft text-[11px] font-bold leading-none"
                  >
                    {sumIssueUnits(sectionIssues)}
                  </span>
                </h3>
                <ValidationIssuesCard
                  issues={sectionIssues}
                  onReassign={onReassign}
                  onCourseFocus={onCourseFocus}
                />
              </section>
            );
          })}
        </div>
      )}

      {canForcePlace && latestRun && (
        <ForcePlaceModal
          scheduleId={scheduleId}
          runId={latestRun.id}
          toleranceMinutes={tolerance}
          isOpen={forcePlaceOpen}
          onClose={() => setForcePlaceOpen(false)}
        />
      )}
    </div>
  );
}
